import {
  createSlice,
  createEntityAdapter,
  EntityState,
  createAsyncThunk,
  createAction,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { TaskListElement, TaskListFilters } from 'types/store/tasksList';
import { ProjectTasksSliceState } from 'types/store/projectTasksList';
import { RootState } from 'state/store';
import {
  setTasksAssignees,
  selectAssigneesListByIds,
} from 'state/TasksList/allTasks/allTasksSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import {
  selectProjectTasksExportState,
  selectSelectedTasksIds,
} from 'state/ProjectTasksList/export/exportSlice';
import { exportTasksList, fetchTasksList } from 'api/projectTasks';
import { getFetchProjectsTasksListQueryParams } from './helpers';
import { SLICE_STATUS, TASKS_TABLE_TABS } from 'utils/constants';
import { normalizeFiltersSettingsVersions } from 'state/TasksList/helpers';
import { DEFAULT_TASK_ELEMENT_PERMISSIONS } from 'utils/constants/centralizedTasks';

interface ProjectTasksListSliceState
  extends Omit<ProjectTasksSliceState, 'tasks'> {
  tasks: EntityState<TaskListElement>;
}

interface FetchTasksListParams {
  projectId: string;
  userId?: string;
  fetchNext?: boolean;
  includeTotalCount?: boolean;
}

const tasksAdapter = createEntityAdapter<TaskListElement>({
  selectId: (task) => task.id,
});

const initialState: ProjectTasksListSliceState = {
  tasks: tasksAdapter.getInitialState(),
  links: {
    next: null,
  },
};

export const fetchProjectTasks = createAsyncThunk(
  'projectTasksList/fetchProjectTasks',
  async (
    params: FetchTasksListParams,
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const projectTasksState = selectProjectTasksListState(
        getState() as RootState
      );

      const queryParams =
        getFetchProjectsTasksListQueryParams<ProjectTasksListSliceState>(
          params,
          projectTasksState
        );

      const response = await fetchTasksList(queryParams);
      dispatch(setTasksAssignees(response.assignees));

      return {
        tasks: response.tasks,
        links: response.links,
        fetchNext: params.fetchNext,
        totalCount: response.totalCount,
        permissions: response.permissions,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching project tasks');
    }
  }
);

export const exportProjectTasks = createAsyncThunk(
  'projectTasksList/exportProjectTasks',
  async (params: FetchTasksListParams, { getState, rejectWithValue }) => {
    try {
      const projectTasksState = selectProjectTasksListState(
        getState() as RootState
      );
      const filters = projectTasksState.filters
        ? { ...projectTasksState.filters }
        : {};
      const actionsState = selectProjectTasksExportState(
        getState() as RootState
      );

      if (!('disabled' in filters)) {
        filters.disabled = true;
      }

      const exportParams: any = {
        ...filters,
        projectId: params.projectId,
        exportAll: actionsState.selectAllTasks,
        excludedIds: actionsState.excludedTasksIds,
        selectedIds: actionsState.selectedTasksIds,
      };

      if (params.userId) {
        exportParams.user = [params.userId];
      }

      const response = await exportTasksList(exportParams);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const reorderTask = createAction<{
  taskId: string;
  startIndex: number;
  endIndex: number;
}>('projectTasksList/reorderTask');

export const updateTasksProjectOrder = createAction<{
  startIndex: number;
  endIndex: number;
  newOrder: number;
}>('projectTasksList/updateTasksProjectOrder');

export const updateTaskInList = createAction<{
  taskId: string;
  updatedTask: Partial<TaskListElement>;
}>('projectTasksList/updateTaskInList');

const projectTasksListSlice = createSlice({
  name: 'projectTasksList',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        search: state.filters?.search,
        ...action.payload,
      };
    },
    setSearchFilter: (state, action) => {
      state.filters = {
        ...state.filters,
        search: action.payload,
      };
    },
    resetFilters: (state) => {
      state.filters = {};
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectTasks.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjectTasks.rejected, (state, action) => {
        state.status = SLICE_STATUS.FAILED;
        throw new Error(action.payload as string);
      })
      .addCase(fetchProjectTasks.fulfilled, (state, action) => {
        const { tasks, links, fetchNext, totalCount, permissions } =
          action.payload;

        if (fetchNext) {
          tasksAdapter.upsertMany(state.tasks, tasks);
          state.permissions = {
            ...state.permissions,
            ...permissions,
          };
        } else {
          tasksAdapter.setAll(state.tasks, tasks);
          state.permissions = permissions;
        }

        state.links = links;
        if (totalCount !== undefined) {
          state.totalCount = totalCount;
        }
        state.status = SLICE_STATUS.IDLE;
      })
      .addCase(reorderTask, (state, action) => {
        if (action.payload) {
          const { taskId, startIndex, endIndex } = action.payload;
          const tasks = tasksAdapter.getSelectors().selectAll(state.tasks);

          if (startIndex < 0 || endIndex > tasks.length) {
            return;
          }

          const taskToReorder = { ...tasks[startIndex] };
          if (taskToReorder.id !== taskId) {
            return;
          }

          tasksAdapter.removeOne(state.tasks, taskId);

          const updatedTasks = tasksAdapter
            .getSelectors()
            .selectAll(state.tasks);
          updatedTasks.splice(endIndex, 0, taskToReorder);
          tasksAdapter.setAll(state.tasks, updatedTasks);
        }
      })
      .addCase(updateTasksProjectOrder, (state, action) => {
        if (action.payload) {
          const { startIndex, endIndex, newOrder } = action.payload;
          const tasks = tasksAdapter.getSelectors().selectAll(state.tasks);

          if (startIndex < 0 || endIndex > tasks.length) {
            return;
          }

          const updatedTasks = tasks.map((task) => ({ ...task }));

          if (endIndex < startIndex) {
            for (let i = startIndex; i > endIndex; i--) {
              updatedTasks[i].projectOrder = (
                Number(updatedTasks[i].projectOrder) + 1
              ).toString();
            }
          } else {
            for (let i = startIndex; i < endIndex; i++) {
              updatedTasks[i].projectOrder = (
                Number(updatedTasks[i].projectOrder) - 1
              ).toString();
            }
          }

          updatedTasks[endIndex].projectOrder = newOrder.toString();
          tasksAdapter.setAll(state.tasks, updatedTasks);
        }
      })
      .addCase(updateTaskInList, (state, action) => {
        if (action.payload) {
          const { taskId, updatedTask } = action.payload;
          const existingTask = tasksAdapter
            .getSelectors()
            .selectById(state.tasks, taskId);
          if (existingTask) {
            tasksAdapter.updateOne(state.tasks, {
              id: taskId,
              changes: updatedTask,
            });
          }
        }
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        if (action.payload) {
          const isTeamTasks =
            action.payload.filter_type === TASKS_TABLE_TABS.TEAM_TASKS;
          const isUserTasks =
            action.payload.filter_type === TASKS_TABLE_TABS.MY_TASKS;

          if (isTeamTasks || isUserTasks) {
            const filterSettings = normalizeFiltersSettingsVersions(
              action.payload.filter_settings
            );
            state.filters = {
              ...state.filters,
              ...filterSettings,
            };
          }
        }
      });
  },
});

export const { setFilters, setSearchFilter, resetFilters, resetState } =
  projectTasksListSlice.actions;

export const selectProjectTasksListState = (state: RootState) =>
  state.projectTasksList.value;

export const selectTaskPermissions = (state: RootState) =>
  selectProjectTasksListState(state).permissions;

export const {
  selectAll: selectAllTasks,
  selectById: selectTaskById,
  selectIds: selectTasksIds,
} = tasksAdapter.getSelectors(
  (state: RootState) => selectProjectTasksListState(state).tasks
);

export const selectProjectTasksLoading = (state: RootState) =>
  selectProjectTasksListState(state).status;

export const selectProjectTasksTableFilters = (state: RootState) =>
  selectProjectTasksListState(state).filters ?? {};

export const selectHasProjectTasksTableFilters = createSelector(
  [selectProjectTasksTableFilters],
  (filters: TaskListFilters) => {
    const { search, ...otherFilters } = filters;
    return Object.keys(otherFilters).length > 0;
  }
);

export const selectHasProjectTasksTableSearchFilter = createSelector(
  [selectProjectTasksTableFilters],
  (filters: TaskListFilters) => !!filters.search
);

export const selectProjectTasks = createSelector(
  [(state: RootState) => state, selectTaskPermissions],
  (
    state: RootState,
    permissions: ProjectTasksListSliceState['permissions']
  ) => {
    const tasks = selectAllTasks(state);
    return tasks.map((task: TaskListElement) => {
      return {
        ...task,
        assignees: selectAssigneesListByIds(state, task.assignees),
        permissions: permissions
          ? permissions[task.id]
          : DEFAULT_TASK_ELEMENT_PERMISSIONS,
      };
    });
  }
);

export const selectSelectedTaskAssigneesCheckedStatus = createSelector(
  [(state: RootState) => state],
  (state) => {
    const selectedTasksIds = selectSelectedTasksIds(state);
    const assigneesCount = new Map<string, number>();
    selectedTasksIds.forEach((taskId) => {
      const task = selectTaskById(state, taskId);
      if (task) {
        task.assignees.forEach((assignee) => {
          if (assigneesCount.has(assignee)) {
            assigneesCount.set(assignee, assigneesCount.get(assignee)! + 1);
          } else {
            assigneesCount.set(assignee, 1);
          }
        });
      }
    });
    return Array.from(assigneesCount.entries()).reduce(
      (acc, [assignee, count]) => {
        if (count === selectedTasksIds.length) {
          acc[assignee] = true;
        } else {
          acc[assignee] = 'mixed';
        }
        return acc;
      },
      {} as Record<string, boolean | 'mixed'>
    );
  }
);

export const selectCanFetchtMoreTasks = (state: RootState) =>
  selectProjectTasksListState(state).links.next !== null;

export const selectTotalCount = (state: RootState) =>
  selectProjectTasksListState(state).totalCount;

export default projectTasksListSlice.reducer;
